// Pending referral code — captured from ?join=CODE, a fexr.io/{token} referral
// link, or typed at signup; held through signup/login, applied once by
// PendingReferralApplier, then cleared. The link token (when known) is kept
// only so the invite field can show fexr.io/{token}. Last touch wins on the
// client; the first referrer is permanent server-side.

const LEGACY_STORAGE_KEY = "pending_referral_code";
const STORAGE_KEY = "fexr_pending_referral_v1";
const COOKIE_NAME = "fexr_ref";
// A code older than this can't succeed anyway (accounts accept codes for 72h).
const REFERRAL_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const CODE_RE = /^[A-Z0-9]{6,12}$/;
// One-link tokens (fexrapi link_aliases.py): six lowercase letters, or a
// custom name of uppercase letters and digits. Case is significant.
const LINK_TOKEN_RE = /^(?:[a-z]{6}|[A-Z0-9]{1,48})$/;

interface StoredReferralCode {
  code: string;
  captured_at: number;
  token?: string;
}

export interface PendingReferral {
  code: string;
  token: string | null;
}

export function normalizeReferralCode(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "");
}

export function isValidReferralCode(value: string): boolean {
  return CODE_RE.test(normalizeReferralCode(value));
}

function writeCookie(record: StoredReferralCode | null) {
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = record
      ? `${COOKIE_NAME}=${encodeURIComponent(
          [record.code, record.captured_at, record.token]
            .filter(Boolean)
            .join("."),
        )}; Max-Age=${Math.floor(
          REFERRAL_MAX_AGE_MS / 1000,
        )}; Path=/; SameSite=Lax${secure}`
      : `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
  } catch {}
}

function readCookie(): StoredReferralCode | null {
  try {
    const match = document.cookie
      .split("; ")
      .find((part) => part.startsWith(`${COOKIE_NAME}=`));
    if (!match) return null;
    const [code, ts, token] = decodeURIComponent(
      match.slice(COOKIE_NAME.length + 1),
    ).split(".");
    const captured_at = Number(ts);
    if (!code || !Number.isFinite(captured_at)) return null;
    return token ? { code, captured_at, token } : { code, captured_at };
  } catch {
    return null;
  }
}

function isFresh(record: Partial<StoredReferralCode> | null): boolean {
  if (!record) return false;
  return (
    CODE_RE.test(normalizeReferralCode(record.code ?? "")) &&
    typeof record.captured_at === "number" &&
    Date.now() - record.captured_at < REFERRAL_MAX_AGE_MS
  );
}

export function isLinkToken(value: string): boolean {
  return LINK_TOKEN_RE.test(value) && !/^[0-9]+$/.test(value);
}

/**
 * Stores a code to apply after sign-up. `token` is the fexr.io/{token} link it
 * came from; when omitted, a token already stored for the same code is kept
 * (e.g. the link entry page stores both, then /register?join=CODE re-captures).
 */
export function storePendingReferralCode(
  value: string,
  token?: string | null,
): boolean {
  if (typeof window === "undefined") return false;

  const code = normalizeReferralCode(value);
  if (!CODE_RE.test(code)) return false;

  const keptToken =
    token === undefined
      ? (() => {
          const prev = readStored();
          return prev && normalizeReferralCode(prev.code ?? "") === code
            ? prev.token
            : undefined;
        })()
      : (token ?? undefined);
  const record: StoredReferralCode = { code, captured_at: Date.now() };
  if (keptToken && isLinkToken(keptToken)) record.token = keptToken;
  let stored = false;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    stored = true;
  } catch {}
  // Backup for Safari ITP storage partitioning.
  writeCookie(record);
  try {
    sessionStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {}
  return stored || readCookie()?.code === code;
}

function readStored(): Partial<StoredReferralCode> | null {
  let record: Partial<StoredReferralCode> | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) record = JSON.parse(raw) as Partial<StoredReferralCode>;
  } catch {}
  return record ?? readCookie();
}

export function getPendingReferralCode(): string | null {
  return getPendingReferral()?.code ?? null;
}

/** The pending code plus the link token it was captured from, if any. */
export function getPendingReferral(): PendingReferral | null {
  if (typeof window === "undefined") return null;

  const record = readStored();

  if (!record) {
    // One-time migration from the old sessionStorage key.
    try {
      const legacy = normalizeReferralCode(
        sessionStorage.getItem(LEGACY_STORAGE_KEY) ?? "",
      );
      if (CODE_RE.test(legacy)) {
        storePendingReferralCode(legacy);
        return { code: legacy, token: null };
      }
    } catch {}
    return null;
  }

  if (!isFresh(record)) {
    clearPendingReferralCode();
    return null;
  }
  const token =
    typeof record.token === "string" && isLinkToken(record.token)
      ? record.token
      : null;
  return { code: normalizeReferralCode(record.code ?? ""), token };
}

export type InviteInput =
  | { kind: "code"; code: string }
  | { kind: "token"; token: string };

/**
 * Reads whatever was pasted into the invite field: fexr.io/{token} (any
 * scheme or host), api.fexr.io/r/CODE, /join/CODE, ?join=CODE, or a bare
 * token. A bare value is a token; the caller falls back to treating it as a
 * legacy code when no link resolves.
 */
export function parseInviteInput(raw: string): InviteInput | null {
  const text = raw.trim();
  if (!text) return null;

  let url: URL | null = null;
  try {
    url = new URL(
      /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`,
    );
  } catch {}

  const looksLikeUrl = /[/.?]/.test(text);
  if (!url || !looksLikeUrl) {
    const bare = text.replace(/^\/+|\/+$/g, "");
    return bare ? { kind: "token", token: bare } : null;
  }

  const join = url.searchParams.get("join");
  if (join) return { kind: "code", code: normalizeReferralCode(join) };

  const [first, second, ...rest] = url.pathname.split("/").filter(Boolean);
  const parts = url.pathname.split("/").filter(Boolean);
  if (second && !rest.length && (first === "r" || first === "join")) {
    return { kind: "code", code: normalizeReferralCode(second) };
  }
  if (first && !second) return { kind: "token", token: first };
  // "fexr.io" alone, or the host without a path yet.
  if (parts.length === 0 && /(^|\.)fexr\.io$/i.test(url.hostname)) return null;
  if (parts.length === 0 && !text.includes("/")) {
    return { kind: "token", token: text };
  }
  return null;
}

export function clearPendingReferralCode(): void {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {}
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
  writeCookie(null);
  try {
    window.dispatchEvent(new Event(PENDING_REFERRAL_EVENT));
  } catch {}
}

/** Fired on window whenever the pending code is captured or cleared. */
export const PENDING_REFERRAL_EVENT = "fexr:pending-referral";

/**
 * Reads ?join=CODE from the current URL, stores it (overwriting any unapplied
 * code), and strips the param so it isn't re-shared or bookmarked. Malformed
 * codes are dropped silently. Returns the captured code, if any.
 */
export function captureReferralFromUrl(): string | null {
  if (typeof window === "undefined") return null;

  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return null;
  }
  const raw = url.searchParams.get("join");
  if (raw === null) return null;

  const code = normalizeReferralCode(raw);
  url.searchParams.delete("join");
  try {
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {}

  if (!CODE_RE.test(code)) return null;
  storePendingReferralCode(code);
  try {
    window.dispatchEvent(new Event(PENDING_REFERRAL_EVENT));
  } catch {}
  return code;
}
